import React, { useState } from "react";
import { ArrowRight, ArrowLeft, X } from "@phosphor-icons/react";
import { Button } from "./ui/Button";
import Logo from "./Logo";
import { getStory } from "../demo/stories";

/**
 * Iter 20.10 — DemoIntroSlideshow
 *
 * Vollbild-Slideshow, die dem User VOR dem Betreten des Split-Screen-Tools
 * die 5 Kontext-Slides zeigt (Persona → CV → SV-Auszug → KV → Los geht's).
 *
 * Nach dem Abschluss (Klick „Tool starten →" oder „Slideshow überspringen")
 * ruft die Komponente `onComplete()` auf. Der Elternteil (DemoToolPage)
 * merkt sich das im sessionStorage, damit die Slideshow bei Refresh nicht
 * noch einmal aufpoppt.
 */
export default function DemoIntroSlideshow({ caseId, caseTitle, onComplete }) {
  const story = getStory(caseId);
  const [idx, setIdx] = useState(0);

  if (!story) {
    // Fallback: unbekannte Case → sofort ins Tool
    onComplete();
    return null;
  }

  const slides = story.introSlides;
  const isLast = idx === slides.length - 1;
  const slide = slides[idx];

  const goNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setIdx(idx + 1);
    }
  };
  const goBack = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  return (
    <div
      className="min-h-screen bg-ink-50 flex flex-col"
      data-testid="demo-intro-slideshow"
    >
      {/* Top-Bar */}
      <div className="border-b border-ink-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size="md" />
            <div className="hidden sm:block text-xs text-warm-500">
              Demo &middot; {caseTitle}
            </div>
          </div>
          <button
            type="button"
            onClick={onComplete}
            className="inline-flex items-center gap-1.5 text-xs text-warm-600 hover:text-ink-900 transition-crisp"
            data-testid="demo-intro-skip"
          >
            <X size={12} weight="bold" />
            Slideshow überspringen
          </button>
        </div>
      </div>

      {/* Progress-Bar */}
      <div className="bg-white border-b border-ink-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-widest text-warm-500 font-semibold whitespace-nowrap">
            {idx + 1} von {slides.length}
          </div>
          <div className="flex-1 flex gap-1">
            {slides.map((_, i) => (
              <div
                key={i}
                className={[
                  "h-1 flex-1 rounded-sm transition-all",
                  i === idx
                    ? "bg-brand-500"
                    : i < idx
                    ? "bg-brand-300"
                    : "bg-ink-200",
                ].join(" ")}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Slide-Content */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 lg:px-10 py-10">
        <div
          key={idx}
          className="animate-in fade-in slide-in-from-bottom-1 duration-300"
          data-testid={`demo-intro-slide-${idx}`}
        >
          <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-500 mb-2">
            Kontext-Info
          </div>
          <h1 className="font-cabinet text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink-900 font-medium leading-tight">
            {slide.title}
          </h1>
          {slide.subtitle && (
            <div className="mt-2 text-base text-warm-600 max-w-3xl">
              {slide.subtitle}
            </div>
          )}
          <div className="mt-7">{slide.body}</div>
        </div>
      </main>

      {/* Navigation-Bar */}
      <div className="border-t border-ink-200 bg-white sticky bottom-0">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <Button
            variant="secondary"
            size="md"
            onClick={goBack}
            disabled={idx === 0}
            data-testid="demo-intro-back"
          >
            <ArrowLeft size={12} weight="bold" />
            Zurück
          </Button>
          <div className="text-[11px] text-warm-500 hidden sm:block">
            {isLast
              ? "Bereit fürs Tool?"
              : "Weiter für den nächsten Kontext-Abschnitt"}
          </div>
          <Button
            variant="brand"
            size="md"
            onClick={goNext}
            data-testid="demo-intro-next"
          >
            {isLast ? "Tool starten" : "Weiter"}
            <ArrowRight size={12} weight="bold" />
          </Button>
        </div>
      </div>
    </div>
  );
}
